import React from 'react';
import './App.css';

type ProgressItem = {
  label: string;
  percent: number;
};

type Milestone = {
  date: string;
  title: string;
  desc: string;
};

type NewsItem = {
  title: string;
  date: string;
  image: string;
  excerpt: string;
};

const progress: ProgressItem[] = [
  { label: 'Core engine', percent: 65 },
  { label: 'Networking', percent: 55 },
  { label: 'Combat system', percent: 40 },
  { label: 'World & quests', percent: 30 },
];

const milestones: Milestone[] = [
  { date: '2025-12-12', title: 'Closed Testing ', desc: 'Small group plus friends & family.' },
  { date: '2026-2-20', title: 'Closed Alpha 2', desc: 'Content pass and focused bug bash.' },
  { date: '2026-3-24', title: 'Open Beta Weekend', desc: 'Server stress tests and live ops rehearsal.' },
  { date: '2026-05-10', title: 'Early Access', desc: 'Founders launch (tentative).' },
];

const news: NewsItem[] = [
  {
    title: 'Basic healing spell implemented',
    date: '2025-08-09',
    image: 'https://www.obligatio.se/image1.gif',
    excerpt: "We've implemented a basic healing spell, giving players the first taste of restorative magic in Obligatio.",
  },
  {
    title: 'Holy Spell Sneak Peek',
    date: '2025-08-09',
    image: 'https://i.gyazo.com/53cedb44f3b49e388bdf792476e8e4a4.gif',
    excerpt: 'Get a glimpse of a holy spell designed for healers.',
  },
  {
    title: 'HUD Preview: Health, Mana & Action Bar',
    date: '2025-08-09',
    image: 'https://www.obligatio.se/image2.png',
    excerpt: 'Our latest HUD design shows the health bar, mana bar, and action bar coming together.',
  },
];

type ActionButtonProps = {
  href: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  icon?: string;
};

const ActionButton: React.FC<ActionButtonProps> = ({ href, children, variant = 'primary', icon }) => (
  <a className={`action-button action-button--${variant}`} href={href}>
    {icon ? (
      <span aria-hidden="true" className="action-button__icon">
        {icon}
      </span>
    ) : null}
    <span>{children}</span>
  </a>
);

const formatDate = (value: string) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const App: React.FC = () => {
  return (
    <div className="landing">
      <header className="landing-header">
        <nav className="landing-nav">
          <div className="brand">
            <img alt="Obligatio logo" className="brand__logo" src="/Obligatio.png" />
            <span>Obligatio</span>
          </div>
          <div className="nav-buttons">
            <ActionButton href="https://login.obligatio.se/" icon="🔐" variant="ghost">
              Log in
            </ActionButton>
            <ActionButton href="https://login.obligatio.se/" icon="⬇️">
              Download
            </ActionButton>
          </div>
        </nav>
      </header>

      <main className="landing-main">
        <section className="hero">
          <div className="hero-content">
            <h1>
              Build the world of <span className="hero-accent">Obligatio</span>
            </h1>
            <p>
              An indie MMORPG where choices matter, spells crackle, and dungeons do not hold your hand. Follow our progress
              and join upcoming playtests as we shape the world together.
            </p>
            <div className="hero-actions">
            <ActionButton href="/playtest.ics" icon="📅">
                  Playtest Schedule
                </ActionButton>
              <ActionButton href="https://login.obligatio.se/" icon="🧙" variant="secondary">
                My account
              </ActionButton>
            </div>
          </div>
          <div className="hero-media">
            <div className="hero-media__frame">
              <span>Gameplay trailer placeholder</span>
            </div>
          </div>
        </section>

        <section aria-labelledby="progress-heading" className="section progress-section">
          <h2 className="section-title" id="progress-heading">
            <span aria-hidden="true" className="section-title__icon">
              ✅
            </span>
            Current progress
          </h2>
          <div className="progress-grid">
            {progress.map((item) => (
              <article className="progress-card" key={item.label}>
                <header className="progress-card__header">
                  <span className="progress-card__label">{item.label}</span>
                  <span className="progress-card__value">{item.percent}%</span>
                </header>
                <div className="progress-card__meter" role="progressbar" aria-valuenow={item.percent} aria-valuemin={0} aria-valuemax={100}>
                  <div className="progress-card__fill" style={{ width: `${item.percent}%` }} />
                </div>
              </article>
            ))}
          </div>
        </section>

        <section aria-labelledby="schedule-heading" className="section schedule-section" id="schedule">
          <h2 className="section-title" id="schedule-heading">
            <span aria-hidden="true" className="section-title__icon">
              📅
            </span>
            Playtest schedule
          </h2>
          <div className="timeline">
            {milestones.map((milestone) => (
              <article className="timeline-item" key={milestone.title}>
                <div aria-hidden="true" className="timeline-item__marker" />
                <div className="timeline-item__content">
                  <div className="timeline-item__date">{formatDate(milestone.date)}</div>
                  <h3>{milestone.title}</h3>
                  <p>{milestone.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section aria-labelledby="news-heading" className="section news-section" id="news">
          <h2 className="section-title" id="news-heading">
            <span aria-hidden="true" className="section-title__icon">
              📰
            </span>
            Dev updates
          </h2>
          <div className="news-grid">
            {news.map((item) => (
              <article className="news-card" key={item.title}>
                <div className="news-card__image">
                  <img alt={item.title} loading="lazy" src={item.image} />
                </div>
                <div className="news-card__body">
                  <div className="news-card__date">{formatDate(item.date)}</div>
                  <h3>{item.title}</h3>
                  <p>{item.excerpt}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default App;
